import React, { useState, useEffect } from "react";
import axios from "axios";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import {
  Workflow, Play, Pause, Plus, Bot, Zap, Clock, CheckCircle, XCircle,
  Mail, Users, FileText, Calendar, TrendingUp, Bell, Send, RefreshCw,
  ChevronRight, Sparkles, MessageSquare, Settings, BarChart3, Activity
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_ICONS = {
  send_email: Mail,
  update_status: RefreshCw,
  create_task: FileText,
  notify_user: Bell,
  ai_evaluate: Sparkles,
  schedule_meeting: Calendar,
  generate_report: BarChart3,
  match_investor: Users,
  update_score: TrendingUp,
  send_reminder: Clock,
  approve_application: CheckCircle,
  request_documents: FileText,
  create_milestone: Zap,
  webhook: Send
};

const STATUS_COLORS = {
  draft: "bg-gray-500",
  active: "bg-green-500",
  paused: "bg-yellow-500",
  completed: "bg-blue-500",
  failed: "bg-red-500",
  running: "bg-purple-500"
};

const WorkflowAutomation = () => {
  const { toast } = useToast();
  const { token, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [customWorkflowDesc, setCustomWorkflowDesc] = useState("");
  const [showCustomDialog, setShowCustomDialog] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboard();
      fetchTemplates();
    }
  }, [isAuthenticated]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/workflows/dashboard`, { headers });
      setDashboard(res.data);
    } catch (e) {
      console.error("Dashboard error:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/workflows/templates`);
      setTemplates(res.data.templates || []);
    } catch (e) {
      console.error("Templates error:", e);
    }
  };

  const createWorkflow = async (templateId, activate = false) => {
    try {
      const res = await axios.post(`${API}/workflows/create`, {
        template_id: templateId,
        activate
      }, { headers });
      toast({ title: "Workflow Created", description: res.data.workflow?.name });
      fetchDashboard();
      setShowCreateDialog(false);
    } catch (e) {
      toast({ title: "Error", description: "Failed to create workflow", variant: "destructive" });
    }
  };

  const executeWorkflow = async (workflowId) => {
    try {
      const res = await axios.post(`${API}/workflows/${workflowId}/execute`, {}, { headers });
      toast({ 
        title: res.data.status === "completed" ? "Workflow Completed" : "Workflow Failed",
        description: `Executed ${res.data.step_results?.length || 0} steps`
      });
      fetchDashboard();
    } catch (e) {
      toast({ title: "Error", description: "Failed to execute workflow", variant: "destructive" });
    }
  };

  const updateWorkflowStatus = async (workflowId, status) => {
    try {
      await axios.put(`${API}/workflows/${workflowId}/status`, { status }, { headers });
      toast({ title: "Status Updated", description: `Workflow ${status}` });
      fetchDashboard();
    } catch (e) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const sendAIMessage = async () => {
    if (!aiInput.trim()) return;
    
    const userMessage = aiInput;
    setAiMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setAiInput("");
    setAiLoading(true);

    try {
      const res = await axios.post(`${API}/workflows/ai/chat`, { message: userMessage }, { headers });
      setAiMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
      
      if (res.data.suggested_action) {
        toast({ title: "AI Suggestion", description: `Try the ${res.data.suggested_action} workflow` });
      }
    } catch (e) {
      setAiMessages(prev => [...prev, { role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setAiLoading(false);
    }
  };

  const createCustomWorkflow = async () => {
    if (!customWorkflowDesc.trim()) return;
    
    try {
      const res = await axios.post(`${API}/workflows/ai/create-custom`, {
        description: customWorkflowDesc
      }, { headers });
      
      if (res.data.workflow_id) {
        toast({ title: "AI Workflow Created", description: res.data.workflow?.name });
        fetchDashboard();
        setShowCustomDialog(false);
        setCustomWorkflowDesc("");
      } else {
        toast({ title: "Error", description: res.data.error || "Failed to create", variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Error", description: "Failed to create custom workflow", variant: "destructive" });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="container mx-auto px-6 py-12 text-center">
          <h1 className="text-2xl text-white mb-4">Please Sign In</h1>
          <Button onClick={() => window.location.href = '/auth'}>Sign In</Button>
        </div>
      </div>
    );
  }

  const metrics = dashboard?.metrics || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Workflow className="h-8 w-8" />
                <h1 className="text-3xl font-bold">AI Workflow Automation</h1>
              </div>
              <p className="opacity-90">Automate your entire accelerator program with AI</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowAIChat(true)} className="bg-white text-purple-600 hover:bg-gray-100">
                <Bot className="h-4 w-4 mr-2" /> AI Assistant
              </Button>
              <Button onClick={() => setShowCreateDialog(true)} variant="outline" className="border-white text-white hover:bg-white/20">
                <Plus className="h-4 w-4 mr-2" /> New Workflow
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <Workflow className="h-6 w-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.total_workflows || 0}</p>
              <p className="text-gray-400 text-xs">Total Workflows</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <Activity className="h-6 w-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.active_workflows || 0}</p>
              <p className="text-gray-400 text-xs">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <Play className="h-6 w-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.total_executions || 0}</p>
              <p className="text-gray-400 text-xs">Executions</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.successful_executions || 0}</p>
              <p className="text-gray-400 text-xs">Successful</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <XCircle className="h-6 w-6 text-red-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.failed_executions || 0}</p>
              <p className="text-gray-400 text-xs">Failed</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-6 w-6 text-cyan-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{metrics.success_rate || 0}%</p>
              <p className="text-gray-400 text-xs">Success Rate</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="workflows" className="space-y-6">
          <TabsList className="bg-gray-800/50 border border-gray-700">
            <TabsTrigger value="workflows" className="data-[state=active]:bg-purple-600">My Workflows</TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-blue-600">Templates</TabsTrigger>
            <TabsTrigger value="executions" className="data-[state=active]:bg-green-600">Executions</TabsTrigger>
            <TabsTrigger value="ai-create" className="data-[state=active]:bg-pink-600">
              <Sparkles className="h-4 w-4 mr-1" /> AI Create
            </TabsTrigger>
          </TabsList>

          {/* My Workflows */}
          <TabsContent value="workflows">
            <div className="space-y-4">
              {(dashboard?.workflows || []).length === 0 ? (
                <Card className="bg-gray-800/50 border-gray-700">
                  <CardContent className="py-12 text-center">
                    <Workflow className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <h3 className="text-xl text-white mb-2">No Workflows Yet</h3>
                    <p className="text-gray-400 mb-4">Create your first workflow from a template or with AI</p>
                    <Button onClick={() => setShowCreateDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="h-4 w-4 mr-2" /> Create Workflow
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                dashboard.workflows.map(workflow => (
                  <Card key={workflow.id} className="bg-gray-800/50 border-gray-700 hover:border-purple-500/50 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-semibold text-white">{workflow.name}</h3>
                            <Badge className={STATUS_COLORS[workflow.status]}>{workflow.status}</Badge>
                            {workflow.ai_generated && (
                              <Badge className="bg-pink-600"><Sparkles className="h-3 w-3 mr-1" /> AI</Badge>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mb-3">{workflow.description}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Zap className="h-4 w-4" /> {workflow.steps?.length || 0} steps
                            </span>
                            <span className="flex items-center gap-1">
                              <Play className="h-4 w-4" /> {workflow.execution_count || 0} runs
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" /> {workflow.trigger_type}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => executeWorkflow(workflow.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Play className="h-4 w-4 mr-1" /> Run
                          </Button>
                          {workflow.status === "active" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateWorkflowStatus(workflow.id, "paused")}
                              className="border-yellow-500 text-yellow-400"
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateWorkflowStatus(workflow.id, "active")}
                              className="border-green-500 text-green-400"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Templates */}
          <TabsContent value="templates">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <Card key={template.id} className="bg-gray-800/50 border-gray-700 hover:border-blue-500/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedTemplate(template)}>
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Workflow className="h-5 w-5 text-blue-400" />
                      {template.name}
                    </CardTitle>
                    <CardDescription>{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">{template.step_count} steps • {template.trigger_type}</span>
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); createWorkflow(template.id, true); }}>
                        <Plus className="h-4 w-4 mr-1" /> Use
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Executions */}
          <TabsContent value="executions">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Recent Executions</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboard?.recent_executions || []).length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No executions yet</p>
                ) : (
                  <div className="space-y-3">
                    {dashboard.recent_executions.map(exec => (
                      <div key={exec.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{exec.workflow_name}</p>
                          <p className="text-gray-500 text-sm">{new Date(exec.started_at).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 text-sm">{exec.step_results?.length || 0} steps</span>
                          <Badge className={STATUS_COLORS[exec.status]}>{exec.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Create */}
          <TabsContent value="ai-create">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-pink-400" />
                  Create Custom Workflow with AI
                </CardTitle>
                <CardDescription>Describe what you want to automate and AI will create it</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Example: When a new application is submitted, automatically evaluate it with AI, and if the score is above 70, approve it and send a welcome email. If below 50, reject it. Otherwise, notify an admin for review."
                  value={customWorkflowDesc}
                  onChange={(e) => setCustomWorkflowDesc(e.target.value)}
                  className="bg-gray-900 border-gray-600 text-white min-h-32 mb-4"
                />
                <Button onClick={createCustomWorkflow} className="bg-pink-600 hover:bg-pink-700" disabled={!customWorkflowDesc.trim()}>
                  <Sparkles className="h-4 w-4 mr-2" /> Generate Workflow
                </Button>
              </CardContent>
            </Card>

            {/* Quick Templates */}
            <div className="mt-6">
              <h3 className="text-white text-lg mb-4">Quick Start Ideas</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Automatically review and score all new applications, approve high scorers",
                  "Send weekly progress reports to all founders every Monday at 9am",
                  "Match startups with investors when they reach certain milestones",
                  "Send reminder emails 3 days before milestone deadlines"
                ].map((idea, i) => (
                  <Card key={i} className="bg-gray-800/30 border-gray-700 cursor-pointer hover:border-pink-500/50"
                    onClick={() => setCustomWorkflowDesc(idea)}>
                    <CardContent className="p-4">
                      <p className="text-gray-300 text-sm">{idea}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Workflow</DialogTitle>
            <DialogDescription>Choose a template or create with AI</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-96 overflow-y-auto">
            {templates.map(template => (
              <div key={template.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg hover:bg-gray-900 cursor-pointer"
                onClick={() => createWorkflow(template.id, true)}>
                <div>
                  <p className="text-white font-medium">{template.name}</p>
                  <p className="text-gray-400 text-sm">{template.description}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-500" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowCustomDialog(true); }}>
              <Sparkles className="h-4 w-4 mr-2" /> Create with AI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Chat Dialog */}
      <Dialog open={showAIChat} onOpenChange={setShowAIChat}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl h-[600px] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-400" />
              AI Workflow Assistant
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {aiMessages.length === 0 && (
              <div className="text-center text-gray-400 py-8">
                <Bot className="h-12 w-12 mx-auto mb-4 text-purple-400" />
                <p>Hi! I can help you automate your accelerator program.</p>
                <p className="text-sm mt-2">Try asking:</p>
                <div className="mt-4 space-y-2">
                  {["Set up automated application review", "Create weekly founder reports", "Help me match startups with investors"].map((q, i) => (
                    <Button key={i} variant="outline" size="sm" className="mr-2" onClick={() => { setAiInput(q); }}>
                      {q}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {aiMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${msg.role === "user" ? "bg-purple-600 text-white" : "bg-gray-700 text-gray-100"}`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {aiLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-700 p-3 rounded-lg">
                  <RefreshCw className="h-5 w-5 animate-spin text-purple-400" />
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-4 border-t border-gray-700">
            <Input
              placeholder="Ask about workflow automation..."
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && sendAIMessage()}
              className="bg-gray-900 border-gray-600 text-white"
            />
            <Button onClick={sendAIMessage} disabled={aiLoading || !aiInput.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowAutomation;
